import { useQuery } from "@apollo/client/react";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { Tooltip } from "@/components/common/Tooltip";
import { buttonClasses } from "@/components/design/button-classes";
import { FilterChip } from "@/components/design/FilterChip";
import { AnalysedDocumentCount } from "@/components/grow/AnalysedDocumentCount";
import { CatalogueCard, type CatalogueEntry, type CatalogueStatus } from "@/components/grow/CatalogueCard";
import { PowerCollage } from "@/components/grow/PowerCollage";
import { SmartAnalysisButton } from "@/components/grow/SmartAnalysisButton";
import { useAnalysedDocumentCount } from "@/components/grow/use-analysed-document-count";
import { TileArtwork } from "@/components/grow/TileArtwork";
import { powerJourneyScale, usePowerQuizProgress } from "@/components/grow/self-report-progress";
import {
  MyGameSessionsDocument,
  PowersDataDocument,
  PublishedQuestionnaireGroupPartsDocument,
  PublishedQuestionnaireGroupsDocument,
} from "@/graphql/generated/operations";
import { ELEMENT_CODES } from "@/lib/elements";
import { gameGroupProgress } from "@/lib/games/game-status";
import { GAME_GROUP_LIST } from "@/lib/games/registry";
import { useGameAccess } from "@/lib/games/use-game-access";
import {
  groupImageSrc,
  isPowerQuizGroup,
  localizedText,
  tileHueFromId,
} from "@/lib/questionnaire-group";
import { POWER_LIBRARY } from "@/lib/power-library";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";

type Filter = CatalogueStatus | "all";
type Entry = CatalogueEntry;

const conceptLink = (chunks: ReactNode[]) => (
  <Link
    id="grow-play-about-powers"
    to="/grow/about-powers"
    className="underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
  >
    {chunks}
  </Link>
);

export function GrowPlayPage() {
  const intl = useIntl();
  const canMap = useHasFeature(FEATURE_CATALOG.growMap.key);
  const canDocuments = useHasFeature(FEATURE_CATALOG.saveDocuments.key);
  const canSelfAssess = useHasFeature(FEATURE_CATALOG.growPlaySelfAssessments.key);
  const canPlay = useGameAccess();
  const gameGroups = GAME_GROUP_LIST.filter((group) => canPlay(group.featureKey));
  const [filter, setFilter] = useState<Filter>("all");
  const {
    data: catalogue,
    loading: catalogueLoading,
    error: catalogueError,
  } = useQuery(PublishedQuestionnaireGroupsDocument);
  const {
    data: groupProgress,
    loading: progressLoading,
    error: progressError,
  } = useQuery(PublishedQuestionnaireGroupPartsDocument, {
    fetchPolicy: "cache-and-network",
  });
  const {
    data: powers,
    loading: powersLoading,
    error: powersError,
  } = useQuery(PowersDataDocument, {
    skip: !canMap && !canSelfAssess,
    fetchPolicy: "cache-and-network",
  });
  const documentCount = useAnalysedDocumentCount(canMap && canDocuments);
  // Every game's sessions in one list; the game group tiles read their progress from it.
  const { data: gameSessions, loading: gameSessionsLoading } = useQuery(MyGameSessionsDocument, {
    skip: gameGroups.length === 0,
    fetchPolicy: "cache-and-network",
  });
  const visibleGroups = (catalogue?.publishedQuestionnaireGroups ?? []).filter(
    (group) => group.itemCount > 0,
  );
  const featured = visibleGroups.find(isPowerQuizGroup) ?? visibleGroups[0];
  const featuredIsPowerQuiz = isPowerQuizGroup(featured);
  const progressById = useMemo(
    () =>
      new Map(
        (groupProgress?.publishedQuestionnaireGroups ?? []).map((group) => [
          group.questionnaireGroupId,
          group,
        ]),
      ),
    [groupProgress],
  );
  const selfByCode = useMemo(
    () =>
      new Map(
        (powers?.selfAssessmentResults ?? []).map((result) => [
          ELEMENT_CODES[String(result.element)],
          result,
        ]),
      ),
    [powers],
  );

  const groupEntries: Entry[] = visibleGroups
    .filter(
      (group) => group.questionnaireGroupId !== featured?.questionnaireGroupId,
    )
    .map((group) => {
      const parts = progressById.get(group.questionnaireGroupId)?.parts ?? [];
      const done = parts.filter((part) => part.answered).length;
      return {
        id: group.questionnaireGroupId,
        title: localizedText(group.name),
        description: localizedText(group.description),
        href: `/grow/play/group/${group.questionnaireGroupId}`,
        kind: "group",
        status: status(done, parts.length),
        progress: `${done} / ${parts.length}`,
        imageSrc: groupImageSrc(group.image) ?? undefined,
        hue: tileHueFromId(group.questionnaireGroupId),
      };
    });
  // A group of games is one tile, read like a questionnaire group: its games
  // with a completed session are its answered parts.
  const gameEntries: Entry[] = gameGroups.map((group) => {
    const { done, total } = gameGroupProgress(
      gameSessions?.myGameSessions ?? [],
      group.games.map((game) => game.key),
    );
    return {
      id: group.key,
      title: intl.formatMessage({ id: group.titleId }),
      description: intl.formatMessage({ id: group.descriptionId }),
      imageSrc: group.imageSrc,
      href: `/grow/play/games/${group.key}`,
      kind: "game",
      status: status(done, total),
      progress: `${done} / ${total}`,
    };
  });
  const powerEntries: Entry[] =
    canSelfAssess && powers
      ? POWER_LIBRARY.map((power) => {
          const self = selfByCode.get(power.abbr);
          const answered = self?.answeredQuestionCount ?? 0;
          const total = self?.questionCount ?? 0;
          return {
            id: power.abbr,
            title: intl.formatMessage({ id: power.nameId }),
            description: "",
            href: `/grow/${Object.keys(ELEMENT_CODES).find((key) => ELEMENT_CODES[key] === power.abbr)}`,
            kind: "power",
            status: status(answered, total),
            progress: total > 0 ? `${answered} / ${total}` : "",
            imageSrc: `/powers/${power.abbr.toLowerCase()}.jpg`,
            imageProgress: { answered, total },
            hue: 0,
          };
        })
      : [];
  const entries = [...groupEntries, ...gameEntries, ...powerEntries];
  const shown =
    filter === "all"
      ? entries
      : entries.filter((entry) => entry.status === filter);
  const featuredParts = featured
    ? (progressById.get(featured.questionnaireGroupId)?.parts ?? [])
    : [];
  const featuredDone = featuredParts.filter((part) => part.answered).length;
  const featuredComplete = featuredParts.length > 0 && featuredDone === featuredParts.length;
  // Home and Map show this same Power Quiz percentage across all published
  // questionnaire parts, not only the featured group's parts.
  const quiz = usePowerQuizProgress();
  const scored =
    canMap && powers
      ? new Set([
          ...powers.selfAssessmentResults
            .filter((r) => typeof r.result === "number")
            .map((r) => String(r.element)),
          ...powers.smartAnalysisResults
            .filter((r) => typeof r.score === "number")
            .map((r) => String(r.element)),
        ]).size
      : null;

  // Scale for the Power Journey figure - in its tooltip now, as on the Map.
  const journey = powerJourneyScale(powers?.selfAssessmentResults);

  useAssistantContext([
    {
      description: "assess catalogue",
      value: `published questionnaire groups: ${visibleGroups.map((g) => localizedText(g.name)).join(", ")}; games: ${gameEntries.map((g) => g.title).join(", ")}; ${canSelfAssess ? "per-Power self-assessments available" : "per-Power self-assessments unavailable"}`,
    },
  ]);
  useAssistantTour(
    visibleGroups.length
      ? {
          label: intl.formatMessage({
            id: "grow.assess.title",
            defaultMessage: "Understand your Power Profile",
          }),
          summary: intl.formatMessage({
            id: "grow.assess.tour",
            defaultMessage: "Choose a questionnaire or a Power to assess.",
          }),
          stops: [
            {
              anchor: "grow.startQuiz",
              title: localizedText(featured?.name),
              description: intl.formatMessage({
                id: "grow.assess.tourFeatured",
                defaultMessage:
                  "Open this questionnaire to answer or resume it.",
              }),
            },
          ],
        }
      : null,
  );

  const pending =
    (catalogueLoading && !catalogue) ||
    (progressLoading && !groupProgress) ||
    (gameSessionsLoading && !gameSessions) ||
    ((canMap || canSelfAssess) && powersLoading && !powers);
  return (
    <main
      className="w-full max-w-[1400px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="PublishedQuestionnaireGroups, PublishedQuestionnaireGroupParts, MyGameSessions, PowersData, PowerAnalysisDocuments"
    >
      <h1 id="grow-play-title" className="smycles-page-title">
        <FormattedMessage
          id="grow.assess.title"
          defaultMessage="Understand your Power Profile"
        />
      </h1>
      <p className="mt-3 max-w-[70ch] text-smycles-body leading-relaxed text-ink-muted">
        {canMap ? canDocuments ? (
          <FormattedMessage id="grow.assess.intro" defaultMessage="Explore your <concept>Powers</concept> through questionnaires, self-assessments and document analysis. Choose where to start or continue." values={{ concept: conceptLink }} />
        ) : (
          <FormattedMessage id="grow.assess.introSelf" defaultMessage="Explore your <concept>Powers</concept> through questionnaires and self-assessments. Choose where to start or continue." values={{ concept: conceptLink }} />
        ) : (
          <FormattedMessage id="grow.assess.introQuestionnaires" defaultMessage="Explore your <concept>Powers</concept> through questionnaires. Choose where to start or continue." values={{ concept: conceptLink }} />
        )}
      </p>

      {canMap || featured ? (
        <section
          className="mt-8 flex flex-wrap items-start gap-x-12 gap-y-5"
          aria-label={intl.formatMessage({
            id: "grow.assess.progress",
            defaultMessage: "Your progress",
          })}
        >
          {canMap && scored !== null ? (
            <StatusItem
              label={
                <FormattedMessage
                  id="grow.assess.powersMeasured"
                  defaultMessage="Powers measured"
                />
              }
              value={`${scored} / ${POWER_LIBRARY.length}`}
            />
          ) : null}
          {featured && groupProgress && quiz.percent !== null ? (
            // Same tooltips as the Map: the two instruments are the same pair on both
            // surfaces, so what explains them there explains them here. They carry the
            // scale the x/n line used to spell out under every figure.
            <Tooltip
              className="cursor-help flex-col items-start"
              content={
                <>
                  <span className="block">
                    {intl.formatMessage(
                      { id: "grow.powerLibrary.powerQuizTooltip" },
                      { count: POWER_LIBRARY.length },
                    )}
                  </span>
                  <span className="mt-1 block tabular-nums text-ink-muted">
                    {intl.formatMessage(
                      {
                        id: "power.instrument.parts",
                        defaultMessage: "{answered} / {total} parts",
                      },
                      { answered: quiz.answered, total: quiz.total },
                    )}
                  </span>
                </>
              }
            >
              <StatusItem
                label={localizedText(featured.name)}
                value={`${quiz.percent} %`}
              />
            </Tooltip>
          ) : null}
          {canMap && powers?.growRanking ? (
            <Tooltip
              className="cursor-help flex-col items-start"
              content={
                <>
                  <span className="block">
                    {intl.formatMessage({ id: "grow.powerLibrary.journeyTooltip" })}
                  </span>
                  <span className="mt-1 block tabular-nums text-ink-muted">
                    {intl.formatMessage(
                      {
                        id: "power.instrument.inDepthStatements",
                        defaultMessage: "{answered} / {total} in-depth statements",
                      },
                      { answered: journey.answered, total: journey.total },
                    )}
                  </span>
                </>
              }
            >
              <StatusItem
                label={
                  <FormattedMessage
                    id="grow.assess.powerJourney"
                    defaultMessage="Power Journey"
                  />
                }
                value={`${powers.growRanking.score} %`}
              />
            </Tooltip>
          ) : null}
          {canMap && canDocuments ? (
            <StatusItem
              label={
                <FormattedMessage
                  id="grow.assess.smartAnalysisDocuments"
                  defaultMessage="Smart Analysis documents"
                />
              }
              value={<AnalysedDocumentCount state={documentCount} compact />}
            />
          ) : null}
        </section>
      ) : null}

      {pending ? (
        <p className="mt-8 text-smycles-body text-ink-muted">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : (catalogueError && !catalogue) ||
        (progressError && !groupProgress) ||
        ((canMap || canSelfAssess) && powersError && !powers) ? (
        <p className="mt-8 text-smycles-body text-ink-muted">
          <FormattedMessage
            id="grow.assess.loadError"
            defaultMessage="Assessments could not be loaded."
          />
        </p>
      ) : (
        <>
          {featured || (canMap && canDocuments) ? (
            <section className="mt-8 grid grid-cols-1 gap-5 @xl:grid-cols-2">
              {featured ? (
                <FeaturedCard
                  id="grow-featured"
                  titleId="grow-featured-name"
                  actionId="grow-featured-start"
                  title={localizedText(featured.name)}
                  description={featuredIsPowerQuiz
                    ? intl.formatMessage({
                        id: "grow.assess.powerQuizDescription",
                        defaultMessage: "A good starting point to explore your Powers. Answer {count} statements about yourself.",
                      }, { count: featured.itemCount })
                    : localizedText(featured.description)}
                  status={
                    featuredParts.length
                      ? featuredIsPowerQuiz
                        ? intl.formatMessage(
                            {
                              id: "grow.assess.partsProgress",
                              defaultMessage: "{done} / {total} {total, plural, one {part} other {parts}}",
                            },
                            { done: featuredDone, total: featuredParts.length },
                          )
                        : `${featuredDone} / ${featuredParts.length}`
                      : ""
                  }
                  href={`/grow/play/group/${featured.questionnaireGroupId}`}
                  eyebrow={featuredIsPowerQuiz ? (
                    <FormattedMessage id="grow.assess.powerQuizEyebrow" defaultMessage="A guided self-assessment" />
                  ) : (
                    <FormattedMessage id="grow.assess.guided" defaultMessage="Guided questionnaire" />
                  )}
                  action={featuredActionLabel(featuredIsPowerQuiz, featuredDone, featuredComplete)}
                  imageSrc={groupImageSrc(featured.image) ?? undefined}
                  powerCollage={
                    featuredIsPowerQuiz && !featured.image
                  }
                  anchor="grow.startQuiz"
                  hue={tileHueFromId(featured.questionnaireGroupId)}
                />
              ) : null}
              {canMap && canDocuments ? (
                <FeaturedCard
                  title={intl.formatMessage({
                    id: "grow.powerDetail.smartAnalysis",
                    defaultMessage: "Smart Analysis",
                  })}
                  description={intl.formatMessage({
                    id: "grow.assess.smartDescription",
                    defaultMessage:
                      "The AI reads documents that describe you and analyses how strong your Powers are.",
                  })}
                  status={<AnalysedDocumentCount state={documentCount} />}
                  eyebrow={
                    <FormattedMessage
                      id="grow.assess.documentInsights"
                      defaultMessage="Insights from documents"
                    />
                  }
                  actionControl={<SmartAnalysisButton id="grow-play-analyse" label={<FormattedMessage id="grow.assess.uploadDocuments" defaultMessage="Upload documents" />} />}
                  hue={118}
                />
              ) : null}
            </section>
          ) : null}
          <section className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-smycles-title font-medium">
                <FormattedMessage
                  id="grow.assess.choose"
                  defaultMessage="Take a closer look"
                />
              </h2>
              <div className="flex flex-wrap gap-2">
                {(
                  ["all", "in-progress", "not-started", "completed"] as const
                ).map((key) => (
                  <FilterChip
                    key={key}
                    id={`grow-filter-${key}`}
                    active={filter === key}
                    onClick={() => setFilter(key)}
                  >
                    {key === "all" ? (
                      <FormattedMessage
                        id="grow.assess.all"
                        defaultMessage="All"
                      />
                    ) : key === "in-progress" ? (
                      <FormattedMessage
                        id="grow.assess.inProgress"
                        defaultMessage="In progress"
                      />
                    ) : key === "not-started" ? (
                      <FormattedMessage
                        id="grow.assess.notStarted"
                        defaultMessage="Not started"
                      />
                    ) : (
                      <FormattedMessage
                        id="grow.assess.completed"
                        defaultMessage="Completed"
                      />
                    )}
                    {` · ${key === "all" ? entries.length : entries.filter((e) => e.status === key).length}`}
                  </FilterChip>
                ))}
              </div>
            </div>
            {shown.length ? (
              <div
                id="grow-play-grid"
                className="mt-4 grid grid-cols-1 gap-5 @sm:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4"
                data-assistant-anchor="grow.play.grid"
              >
                {shown.map((entry) => (
                  <CatalogueCard key={entry.id} entry={entry} />
                ))}
              </div>
            ) : (
              <p className="mt-4 text-smycles-body text-ink-muted">
                <FormattedMessage
                  id="grow.assess.noMatches"
                  defaultMessage="No assessments match this filter."
                />
              </p>
            )}
          </section>
        </>
      )}
    </main>
  );
}

function status(done: number, total: number): CatalogueStatus {
  return done === 0
    ? "not-started"
    : total > 0 && done === total
      ? "completed"
      : "in-progress";
}

// Label over figure, nothing underneath. The x/n scales and method captions that used
// to sit below each value restated what the label and the figure already say; the two
// self-report instruments carry their explanation in the same tooltip the Map uses.
function featuredActionLabel(powerQuiz: boolean, done: number, complete: boolean): ReactNode {
  if (powerQuiz) {
    if (complete) return <FormattedMessage id="grow.assess.powerQuizReview" defaultMessage="Review Power Quiz" />;
    if (done > 0) return <FormattedMessage id="grow.powerDetail.quiz.continue" defaultMessage="Continue Power Quiz" />;
    return <FormattedMessage id="grow.powerDetail.quiz.start" defaultMessage="Start Power Quiz" />;
  }
  if (complete) return <FormattedMessage id="grow.assess.review" defaultMessage="Review questionnaire" />;
  if (done > 0) return <FormattedMessage id="grow.assess.continue" defaultMessage="Continue questionnaire" />;
  return <FormattedMessage id="grow.assess.start" defaultMessage="Start questionnaire" />;
}

function StatusItem({
  label,
  value,
}: {
  label: ReactNode;
  value: ReactNode;
}) {
  return (
    <div className="min-w-36">
      <p className="smycles-field-label">{label}</p>
      <p className="mt-1 font-serif text-smycles-display-s leading-none tabular-nums">
        {value}
      </p>
    </div>
  );
}

function FeaturedCard({
  id,
  titleId,
  actionId,
  title,
  description,
  status,
  href,
  eyebrow,
  action,
  actionControl,
  imageSrc,
  powerCollage = false,
  anchor,
  hue,
}: {
  title: string;
  description: string;
  status: ReactNode;
  href?: string;
  eyebrow: ReactNode;
  action?: ReactNode;
  actionControl?: ReactNode;
  imageSrc?: string;
  powerCollage?: boolean;
  anchor?: string;
  hue: number;
  // Test anchors. Optional: only the questionnaire-group card is addressed by a
  // spec, so only that call site passes them - an id with no consumer is not
  // maintained (AGENTS.md, "Test anchors before tests").
  id?: string;
  titleId?: string;
  // The card root is a <section>, not a control: clicking it navigates nowhere.
  // The action link is the only way in, so a spec that opens the featured group
  // has to address it directly.
  actionId?: string;
}) {
  return (
    <section
      id={id}
      className="box grid min-h-48 overflow-hidden bg-surface @md:grid-cols-[176px_minmax(0,1fr)]"
    >
      <div className="flex min-h-40 items-center justify-center overflow-hidden p-4">
        {powerCollage ? (
          <PowerCollage className="aspect-square w-full rounded-md ring-1 ring-ink/10" />
        ) : (
          <TileArtwork
            imageSrc={imageSrc}
            hue={hue}
            variant="grid"
            className="aspect-square w-full rounded-md ring-1 ring-ink/10"
          />
        )}
      </div>
      <div className="flex min-w-0 flex-col p-5">
        <p className="smycles-field-label">{eyebrow}</p>
        <h2 id={titleId} className="mt-2 text-smycles-title font-medium">
          {title}
        </h2>
        {description ? (
          <p className="mt-2 max-w-[50ch] text-smycles-body leading-relaxed text-ink-muted">
            {description}
          </p>
        ) : null}
        <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-4">
          <span className="text-smycles-meta text-ink-muted">{status}</span>
          {actionControl ?? (href ? (
            <Link
              id={actionId}
              to={href}
              data-assistant-anchor={anchor}
              className={buttonClasses({ variant: "secondary", size: "compact" })}
            >
              {action}
            </Link>
          ) : null)}
        </div>
      </div>
    </section>
  );
}
